import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import type {
  Activity,
  Contact,
  DialList,
  DialListEntry,
  DialListEntryStatus,
  NewActivity,
  NewContact,
  NewDialList,
  PhoneNumber,
  TenantSettings,
  TenantSettingsPatch
} from '@personical/cc-shared-types';
import {
  TERMINAL_DIAL_STATUSES,
  contactMatchesSearch,
  hasAddress,
  normaliseAddress,
  normaliseDepartment
} from '@personical/cc-shared-types';
import { apiUrl } from './apiBase.js';
import { DEMO_TENANT_ID, DEMO_USER_OID } from './auth.js';

function applyVisibility(input: NewContact, ownerId: string): { visibility: 'shared' | 'private'; ownerId?: string } {
  if (input.visibility === 'private') return { visibility: 'private', ownerId };
  return { visibility: 'shared', ownerId: undefined };
}

function normalisePhones(
  phones: PhoneNumber[],
  defaultCountry: string
): PhoneNumber[] {
  return phones.map((p) => {
    const parsed = parsePhoneNumberFromString(p.number, defaultCountry as CountryCode);
    if (parsed?.isValid()) {
      // national: same search key the API stores (shared-types search.ts).
      return { ...p, e164: parsed.number, national: parsed.formatNational().replace(/\D+/g, '') };
    }
    const { e164: _drop, national: _n, ...rest } = p;
    return rest;
  });
}

/**
 * Demo mode's copy of the API's department and address rules: validated
 * the same way (shared-types address.ts), null or blank clears, and on an
 * update a key that's left out keeps what's stored.
 */
function demoExtras(
  input: NewContact,
  existing?: Contact
): { department: string | undefined; address: Contact['address'] } {
  const department =
    'department' in input ? normaliseDepartment(input.department) : existing?.department;
  let address = existing?.address;
  if ('address' in input) {
    const result = normaliseAddress(input.address);
    if (!result.ok) throw new Error(result.error);
    address = result.address;
  }
  return { department, address };
}

export interface ListResult {
  items: Contact[];
  continuationToken: string | null;
}

export interface ListOptions {
  query?: string;
  continuationToken?: string;
  limit?: number;
  /** When true, the list endpoint returns soft-deleted contacts only. */
  trashed?: boolean;
  /** Filter by ContactKind. */
  kind?: 'person' | 'company';
}

export interface ApiClient {
  list(options?: ListOptions): Promise<ListResult>;
  get(id: string): Promise<Contact>;
  create(input: NewContact): Promise<Contact>;
  update(id: string, input: NewContact, options?: { etag?: string }): Promise<Contact>;
  /** Soft-delete (move to trash). Pass hard=true to remove permanently. */
  remove(id: string, options?: { hard?: boolean }): Promise<void>;
  /** Restore a soft-deleted contact. */
  restore(id: string): Promise<Contact>;
  listAuditLog(options?: { continuationToken?: string }): Promise<{ items: Activity[]; continuationToken: string | null }>;
  /** Optimistic batch create — returns per-row results. */
  createMany(inputs: NewContact[]): Promise<{ created: Contact[]; errors: { input: NewContact; error: string }[] }>;
  /** Favourite/unfavourite for the current user. Idempotent. */
  setFavorite(id: string, favorite: boolean): Promise<Contact>;
  /** Set the calling user's private nickname for this contact (empty string clears it). */
  setNickname(id: string, nickname: string): Promise<Contact>;
  /** Look up contacts by phone or email. */
  lookup(query: { phoneNumber?: string; email?: string }): Promise<Contact[]>;
  /** Re-parse every contact's phone numbers using the current default country. */
  renormaliseAll(): Promise<{ total: number; updated: number; skipped: number }>;
  /** Look for likely duplicates of the given inputs by E.164 phone or email. */
  checkDuplicates(input: {
    phoneNumbers?: { number: string }[];
    emails?: { address: string }[];
    excludeId?: string;
  }): Promise<DuplicateMatch[]>;
  /** Merge mergeId into keepId. Drops mergeId, keeps keepId. */
  merge(keepId: string, mergeId: string): Promise<Contact>;
  /** Upload a contact photo (≤ 5 MB JPEG/PNG/WebP/GIF/HEIC). */
  uploadPhoto(id: string, file: Blob): Promise<Contact>;
  /** Remove the contact's photo (and the underlying blob). */
  deletePhoto(id: string): Promise<Contact>;
  /**
   * Send a business-card image to Azure AI Document Intelligence and
   * receive a parsed NewContact suggestion. Real mode hits the func; demo
   * mode rejects.
   */
  scanBusinessCard(file: Blob): Promise<Partial<NewContact>>;
  getSettings(): Promise<TenantSettings>;
  updateSettings(patch: TenantSettingsPatch): Promise<TenantSettings>;
  listActivities(contactId: string): Promise<Activity[]>;
  createActivity(contactId: string, input: NewActivity): Promise<Activity>;
  deleteActivity(activityId: string): Promise<void>;

  listDialLists(): Promise<DialList[]>;
  createDialList(input: NewDialList): Promise<DialList>;
  getDialList(id: string): Promise<DialList & { entries: DialListEntry[] }>;
  updateDialList(id: string, patch: Partial<NewDialList>): Promise<DialList>;
  deleteDialList(id: string): Promise<void>;
  addDialListEntries(id: string, contactIds: string[]): Promise<DialListEntry[]>;
  patchDialListEntry(
    listId: string,
    entryId: string,
    patch: { status?: DialListEntryStatus; notes?: string; recordAttempt?: boolean }
  ): Promise<DialListEntry>;
  deleteDialListEntry(listId: string, entryId: string): Promise<void>;
}

export interface ApiOptions {
  demo: boolean;
  getIdToken(opts?: { forceRefresh?: boolean }): Promise<string | null>;
}

/** Dispatched on the global window when the API gives up on a token —
 *  the SPA's session-expired banner listens for this and prompts the
 *  user to sign in again. */
export const SESSION_EXPIRED_EVENT = 'cc:session-expired';

export interface DuplicateMatch {
  id: string;
  displayName: string;
  reason: 'phone' | 'email';
  matchedValue: string;
}

export class UnauthorizedError extends Error {
  constructor() {
    super('Your session has expired. Please sign in again.');
    this.name = 'UnauthorizedError';
  }
}

class HttpApiClient implements ApiClient {
  constructor(private readonly options: ApiOptions) {}

  private async send(path: string, init: RequestInit, token: string | null) {
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (init.body) headers.set('Content-Type', 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetch(apiUrl(path), { ...init, headers });
  }

  private async fetch<T>(path: string, init: RequestInit = {}): Promise<T> {
    let token = await this.options.getIdToken();
    let res = await this.send(path, init, token);
    // 401 might just mean MSAL handed us a stale cached token. Retry
    // once with forceRefresh — if Entra still considers the session
    // alive, this hands back a fresh token and the call succeeds. If
    // it's a real expiry (revoked, signed out elsewhere), we surface
    // the SESSION_EXPIRED_EVENT so the UI can offer a one-click re-
    // sign-in instead of leaving the page in a broken state.
    if (res.status === 401) {
      token = await this.options.getIdToken({ forceRefresh: true });
      if (token) res = await this.send(path, init, token);
    }
    if (res.status === 401) {
      try {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      } catch {
        // SSR / no window — ignore.
      }
      throw new UnauthorizedError();
    }
    if (!res.ok) {
      let message = `${res.status} ${res.statusText}`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body.error) message = body.error;
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  async list(options: ListOptions = {}): Promise<ListResult> {
    const params = new URLSearchParams();
    if (options.query) params.set('q', options.query);
    if (options.continuationToken) params.set('continuation', options.continuationToken);
    if (options.limit) params.set('limit', String(options.limit));
    if (options.trashed) params.set('trashed', '1');
    if (options.kind) params.set('kind', options.kind);
    const qs = params.toString();
    return this.fetch<ListResult>(`/api/contacts${qs ? `?${qs}` : ''}`);
  }

  get(id: string): Promise<Contact> {
    return this.fetch<Contact>(`/api/contacts/${encodeURIComponent(id)}`);
  }

  create(input: NewContact): Promise<Contact> {
    return this.fetch<Contact>('/api/contacts', {
      method: 'POST',
      body: JSON.stringify(input)
    });
  }

  update(id: string, input: NewContact, options: { etag?: string } = {}): Promise<Contact> {
    const body = options.etag ? { ...input, _etag: options.etag } : input;
    return this.fetch<Contact>(`/api/contacts/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    });
  }

  remove(id: string, options: { hard?: boolean } = {}): Promise<void> {
    const qs = options.hard ? '?hard=1' : '';
    return this.fetch<void>(`/api/contacts/${encodeURIComponent(id)}${qs}`, {
      method: 'DELETE'
    });
  }

  restore(id: string): Promise<Contact> {
    return this.fetch<Contact>(`/api/contacts/${encodeURIComponent(id)}/restore`, {
      method: 'POST'
    });
  }

  async listAuditLog(options: { continuationToken?: string } = {}) {
    const params = new URLSearchParams({ audit: '1' });
    if (options.continuationToken) params.set('continuation', options.continuationToken);
    return this.fetch<{ items: Activity[]; continuationToken: string | null }>(
      `/api/activities?${params.toString()}`
    );
  }

  async createMany(inputs: NewContact[]) {
    const created: Contact[] = [];
    const errors: { input: NewContact; error: string }[] = [];
    for (const input of inputs) {
      try {
        created.push(await this.create(input));
      } catch (err) {
        errors.push({ input, error: (err as Error).message });
      }
    }
    return { created, errors };
  }

  setFavorite(id: string, favorite: boolean): Promise<Contact> {
    return this.fetch<Contact>(`/api/contacts/${encodeURIComponent(id)}/favorite`, {
      method: favorite ? 'POST' : 'DELETE'
    });
  }

  setNickname(id: string, nickname: string): Promise<Contact> {
    return this.fetch<Contact>(`/api/contacts/${encodeURIComponent(id)}/nickname`, {
      method: 'PUT',
      body: JSON.stringify({ nickname })
    });
  }

  async listActivities(contactId: string): Promise<Activity[]> {
    const result = await this.fetch<{ items: Activity[]; continuationToken: string | null }>(
      `/api/contacts/${encodeURIComponent(contactId)}/activities`
    );
    return result.items;
  }

  createActivity(contactId: string, input: NewActivity): Promise<Activity> {
    return this.fetch<Activity>(`/api/contacts/${encodeURIComponent(contactId)}/activities`, {
      method: 'POST',
      body: JSON.stringify(input)
    });
  }

  deleteActivity(activityId: string): Promise<void> {
    return this.fetch<void>(`/api/activities/${encodeURIComponent(activityId)}`, {
      method: 'DELETE'
    });
  }

  async listDialLists(): Promise<DialList[]> {
    const r = await this.fetch<{ items: DialList[] }>('/api/dial-lists');
    return r.items;
  }

  createDialList(input: NewDialList): Promise<DialList> {
    return this.fetch<DialList>('/api/dial-lists', {
      method: 'POST',
      body: JSON.stringify(input)
    });
  }

  getDialList(id: string): Promise<DialList & { entries: DialListEntry[] }> {
    return this.fetch<DialList & { entries: DialListEntry[] }>(
      `/api/dial-lists/${encodeURIComponent(id)}`
    );
  }

  updateDialList(id: string, patch: Partial<NewDialList>): Promise<DialList> {
    return this.fetch<DialList>(`/api/dial-lists/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(patch)
    });
  }

  deleteDialList(id: string): Promise<void> {
    return this.fetch<void>(`/api/dial-lists/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }

  async addDialListEntries(id: string, contactIds: string[]): Promise<DialListEntry[]> {
    const r = await this.fetch<{ items: DialListEntry[] }>(
      `/api/dial-lists/${encodeURIComponent(id)}/entries`,
      { method: 'POST', body: JSON.stringify({ contactIds }) }
    );
    return r.items;
  }

  patchDialListEntry(
    listId: string,
    entryId: string,
    patch: { status?: DialListEntryStatus; notes?: string; recordAttempt?: boolean }
  ): Promise<DialListEntry> {
    return this.fetch<DialListEntry>(
      `/api/dial-lists/${encodeURIComponent(listId)}/entries/${encodeURIComponent(entryId)}`,
      { method: 'PATCH', body: JSON.stringify(patch) }
    );
  }

  deleteDialListEntry(listId: string, entryId: string): Promise<void> {
    return this.fetch<void>(
      `/api/dial-lists/${encodeURIComponent(listId)}/entries/${encodeURIComponent(entryId)}`,
      { method: 'DELETE' }
    );
  }

  async lookup(query: { phoneNumber?: string; email?: string }): Promise<Contact[]> {
    const result = await this.fetch<{ items: Contact[] }>('/api/contacts/lookup', {
      method: 'POST',
      body: JSON.stringify(query)
    });
    return result.items;
  }

  async renormaliseAll() {
    return this.fetch<{ total: number; updated: number; skipped: number }>(
      '/api/contacts/renormalise',
      { method: 'POST' }
    );
  }

  async checkDuplicates(input: {
    phoneNumbers?: { number: string }[];
    emails?: { address: string }[];
    excludeId?: string;
  }): Promise<DuplicateMatch[]> {
    const result = await this.fetch<{ matches: DuplicateMatch[] }>(
      '/api/contacts/check-duplicates',
      { method: 'POST', body: JSON.stringify(input) }
    );
    return result.matches;
  }

  merge(keepId: string, mergeId: string): Promise<Contact> {
    return this.fetch<Contact>('/api/contacts/merge', {
      method: 'POST',
      body: JSON.stringify({ keepId, mergeId })
    });
  }

  async uploadPhoto(id: string, file: Blob): Promise<Contact> {
    // We send the raw bytes — the func reads req.arrayBuffer() and
    // dispatches by Content-Type. fetch() doesn't auto-set Content-Type
    // for Blobs reliably, so we pass it explicitly.
    const token = await this.options.getIdToken();
    const headers = new Headers();
    headers.set('Accept', 'application/json');
    headers.set('Content-Type', file.type || 'application/octet-stream');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const res = await fetch(apiUrl(`/api/contacts/${encodeURIComponent(id)}/photo`), {
      method: 'POST',
      headers,
      body: file
    });
    if (res.status === 401) throw new UnauthorizedError();
    if (!res.ok) {
      let message = `${res.status} ${res.statusText}`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body.error) message = body.error;
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    return (await res.json()) as Contact;
  }

  deletePhoto(id: string): Promise<Contact> {
    return this.fetch<Contact>(`/api/contacts/${encodeURIComponent(id)}/photo`, {
      method: 'DELETE'
    });
  }

  async scanBusinessCard(file: Blob): Promise<Partial<NewContact>> {
    const token = await this.options.getIdToken();
    const headers = new Headers();
    headers.set('Accept', 'application/json');
    headers.set('Content-Type', file.type || 'application/octet-stream');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const res = await fetch(apiUrl('/api/contacts/scan'), { method: 'POST', headers, body: file });
    if (res.status === 401) throw new UnauthorizedError();
    if (!res.ok) {
      let message = `${res.status} ${res.statusText}`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body.error) message = body.error;
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    return (await res.json()) as Partial<NewContact>;
  }

  getSettings(): Promise<TenantSettings> {
    return this.fetch<TenantSettings>('/api/settings');
  }

  updateSettings(patch: TenantSettingsPatch): Promise<TenantSettings> {
    return this.fetch<TenantSettings>('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(patch)
    });
  }
}

const DEMO_KEY = 'cc.demo.contacts';
const DEMO_SETTINGS_KEY = 'cc.demo.settings';
const DEMO_ACTIVITIES_KEY = 'cc.demo.activities';
const DEMO_DIAL_LISTS_KEY = 'cc.demo.dial-lists';
const DEMO_DIAL_ENTRIES_KEY = 'cc.demo.dial-entries';

class DemoApiClient implements ApiClient {
  private read(): Contact[] {
    try {
      const raw = localStorage.getItem(DEMO_KEY);
      if (!raw) return seed();
      return JSON.parse(raw) as Contact[];
    } catch {
      return [];
    }
  }

  private write(contacts: Contact[]) {
    localStorage.setItem(DEMO_KEY, JSON.stringify(contacts));
  }

  async list(options: ListOptions = {}): Promise<ListResult> {
    const all = this.read().filter(
      (c) => c.visibility !== 'private' || c.ownerId === DEMO_USER_OID
    );
    const kindFiltered = options.kind
      ? all.filter((c) => (c.kind ?? 'person') === options.kind)
      : all;
    const tombFiltered = kindFiltered.filter((c) =>
      options.trashed ? !!c.deletedAt : !c.deletedAt
    );
    // Same rules as the API's search (shared-types search.ts).
    const q = options.query?.trim();
    const filtered = q ? tombFiltered.filter((c) => contactMatchesSearch(c, q)) : tombFiltered;
    const sorted = filtered.sort((a, b) => a.displayName.localeCompare(b.displayName));
    return { items: sorted, continuationToken: null };
  }

  async get(id: string): Promise<Contact> {
    const found = this.read().find((c) => c.id === id);
    if (!found) throw new Error('Not found');
    return found;
  }

  async create(input: NewContact): Promise<Contact> {
    const all = this.read();
    const settings = await this.getSettings();
    const now = new Date().toISOString();
    const vis = applyVisibility(input, DEMO_USER_OID);
    const created: Contact = {
      id: crypto.randomUUID(),
      tenantId: DEMO_TENANT_ID,
      ...input,
      ...demoExtras(input),
      phoneNumbers: normalisePhones(input.phoneNumbers, settings.defaultCountry),
      favoritedBy: [],
      nicknames: {},
      visibility: vis.visibility,
      ownerId: vis.ownerId,
      createdAt: now,
      updatedAt: now,
      createdBy: DEMO_USER_OID,
      updatedBy: DEMO_USER_OID
    };
    all.push(created);
    this.write(all);
    return created;
  }

  async update(id: string, input: NewContact, _options: { etag?: string } = {}): Promise<Contact> {
    const all = this.read();
    const idx = all.findIndex((c) => c.id === id);
    if (idx === -1) throw new Error('Not found');
    const existing = all[idx]!;
    const settings = await this.getSettings();
    const nextVisibility: 'shared' | 'private' = input.visibility ?? existing.visibility ?? 'shared';
    let nextOwnerId = existing.ownerId;
    if (existing.visibility === 'private' && nextVisibility === 'private') {
      nextOwnerId = existing.ownerId;
    } else if (nextVisibility === 'private') {
      nextOwnerId = DEMO_USER_OID;
    } else {
      nextOwnerId = undefined;
    }
    const updated: Contact = {
      ...existing,
      ...input,
      ...demoExtras(input, existing),
      phoneNumbers: normalisePhones(input.phoneNumbers, settings.defaultCountry),
      favoritedBy: existing.favoritedBy ?? [],
      nicknames: existing.nicknames ?? {},
      visibility: nextVisibility,
      ownerId: nextOwnerId,
      id: existing.id,
      tenantId: existing.tenantId,
      createdAt: existing.createdAt,
      createdBy: existing.createdBy,
      updatedAt: new Date().toISOString(),
      updatedBy: DEMO_USER_OID
    };
    all[idx] = updated;
    this.write(all);
    return updated;
  }

  async setFavorite(id: string, favorite: boolean): Promise<Contact> {
    const all = this.read();
    const idx = all.findIndex((c) => c.id === id);
    if (idx === -1) throw new Error('Not found');
    const existing = all[idx]!;
    const current = new Set(existing.favoritedBy ?? []);
    if (favorite) current.add(DEMO_USER_OID);
    else current.delete(DEMO_USER_OID);
    const updated: Contact = { ...existing, favoritedBy: [...current] };
    all[idx] = updated;
    this.write(all);
    return updated;
  }

  async setNickname(id: string, nickname: string): Promise<Contact> {
    const all = this.read();
    const idx = all.findIndex((c) => c.id === id);
    if (idx === -1) throw new Error('Not found');
    const existing = all[idx]!;
    const nicknames = { ...(existing.nicknames ?? {}) };
    const trimmed = nickname.trim();
    if (trimmed) nicknames[DEMO_USER_OID] = trimmed;
    else delete nicknames[DEMO_USER_OID];
    const updated: Contact = { ...existing, nicknames };
    all[idx] = updated;
    this.write(all);
    return updated;
  }

  async listActivities(contactId: string): Promise<Activity[]> {
    const raw = localStorage.getItem(DEMO_ACTIVITIES_KEY);
    const all = raw ? (JSON.parse(raw) as Activity[]) : [];
    return all
      .filter((a) => a.contactId === contactId)
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }

  async createActivity(contactId: string, input: NewActivity): Promise<Activity> {
    const raw = localStorage.getItem(DEMO_ACTIVITIES_KEY);
    const all = raw ? (JSON.parse(raw) as Activity[]) : [];
    const created: Activity = {
      ...input,
      id: crypto.randomUUID(),
      tenantId: DEMO_TENANT_ID,
      contactId,
      createdAt: new Date().toISOString(),
      createdBy: DEMO_USER_OID
    };
    all.push(created);
    localStorage.setItem(DEMO_ACTIVITIES_KEY, JSON.stringify(all));
    return created;
  }

  async deleteActivity(activityId: string): Promise<void> {
    const raw = localStorage.getItem(DEMO_ACTIVITIES_KEY);
    const all = raw ? (JSON.parse(raw) as Activity[]) : [];
    const next = all.filter((a) => a.id !== activityId);
    localStorage.setItem(DEMO_ACTIVITIES_KEY, JSON.stringify(next));
  }

  private readDialLists(): DialList[] {
    const raw = localStorage.getItem(DEMO_DIAL_LISTS_KEY);
    return raw ? (JSON.parse(raw) as DialList[]) : [];
  }
  private writeDialLists(v: DialList[]) {
    localStorage.setItem(DEMO_DIAL_LISTS_KEY, JSON.stringify(v));
  }
  private readDialEntries(): DialListEntry[] {
    const raw = localStorage.getItem(DEMO_DIAL_ENTRIES_KEY);
    return raw ? (JSON.parse(raw) as DialListEntry[]) : [];
  }
  private writeDialEntries(v: DialListEntry[]) {
    localStorage.setItem(DEMO_DIAL_ENTRIES_KEY, JSON.stringify(v));
  }

  async listDialLists(): Promise<DialList[]> {
    return this.readDialLists()
      .filter((l) => !l.deletedAt && (l.visibility !== 'private' || l.ownerId === DEMO_USER_OID))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async createDialList(input: NewDialList): Promise<DialList> {
    const all = this.readDialLists();
    const now = new Date().toISOString();
    const created: DialList = {
      id: crypto.randomUUID(),
      tenantId: DEMO_TENANT_ID,
      name: input.name,
      description: input.description,
      visibility: input.visibility,
      ownerId: input.visibility === 'private' ? DEMO_USER_OID : undefined,
      createdAt: now,
      updatedAt: now,
      createdBy: DEMO_USER_OID,
      updatedBy: DEMO_USER_OID
    };
    all.push(created);
    this.writeDialLists(all);
    return created;
  }

  async getDialList(id: string): Promise<DialList & { entries: DialListEntry[] }> {
    const list = this.readDialLists().find((l) => l.id === id);
    if (!list || list.deletedAt) throw new Error('Not found');
    const entries = this.readDialEntries()
      .filter((e) => e.listId === id)
      .sort((a, b) => a.position - b.position);
    return { ...list, entries };
  }

  async updateDialList(id: string, patch: Partial<NewDialList>): Promise<DialList> {
    const all = this.readDialLists();
    const idx = all.findIndex((l) => l.id === id);
    if (idx === -1) throw new Error('Not found');
    const existing = all[idx]!;
    const visibility = patch.visibility ?? existing.visibility;
    const updated: DialList = {
      ...existing,
      name: patch.name ?? existing.name,
      description: patch.description ?? existing.description,
      visibility,
      ownerId: visibility === 'private' ? existing.ownerId ?? DEMO_USER_OID : undefined,
      updatedAt: new Date().toISOString(),
      updatedBy: DEMO_USER_OID
    };
    all[idx] = updated;
    this.writeDialLists(all);
    return updated;
  }

  async deleteDialList(id: string): Promise<void> {
    const all = this.readDialLists();
    const idx = all.findIndex((l) => l.id === id);
    if (idx === -1) return;
    all[idx] = { ...all[idx]!, deletedAt: new Date().toISOString(), deletedBy: DEMO_USER_OID };
    this.writeDialLists(all);
  }

  async addDialListEntries(id: string, contactIds: string[]): Promise<DialListEntry[]> {
    const contacts = this.read();
    const entries = this.readDialEntries();
    let pos = entries.filter((e) => e.listId === id).reduce((m, e) => Math.max(m, e.position), 0);
    const created: DialListEntry[] = [];
    const now = new Date().toISOString();
    for (const cid of contactIds) {
      if (!contacts.find((c) => c.id === cid && !c.deletedAt)) continue;
      pos++;
      const e: DialListEntry = {
        id: crypto.randomUUID(),
        tenantId: DEMO_TENANT_ID,
        listId: id,
        contactId: cid,
        status: 'pending',
        attempts: 0,
        position: pos,
        addedAt: now,
        addedBy: DEMO_USER_OID
      };
      entries.push(e);
      created.push(e);
    }
    this.writeDialEntries(entries);
    return created;
  }

  async patchDialListEntry(
    _listId: string,
    entryId: string,
    patch: { status?: DialListEntryStatus; notes?: string; recordAttempt?: boolean }
  ): Promise<DialListEntry> {
    const all = this.readDialEntries();
    const idx = all.findIndex((e) => e.id === entryId);
    if (idx === -1) throw new Error('Not found');
    const existing = all[idx]!;
    const now = new Date().toISOString();
    const status = patch.status ?? existing.status;
    const updated: DialListEntry = {
      ...existing,
      status,
      notes: patch.notes !== undefined ? patch.notes.trim() || undefined : existing.notes,
      attempts: patch.recordAttempt ? existing.attempts + 1 : existing.attempts,
      lastAttemptAt: patch.recordAttempt ? now : existing.lastAttemptAt,
      completedAt: TERMINAL_DIAL_STATUSES.includes(status)
        ? existing.completedAt ?? now
        : undefined
    };
    all[idx] = updated;
    this.writeDialEntries(all);
    return updated;
  }

  async deleteDialListEntry(_listId: string, entryId: string): Promise<void> {
    const all = this.readDialEntries().filter((e) => e.id !== entryId);
    this.writeDialEntries(all);
  }

  async lookup(query: { phoneNumber?: string; email?: string }): Promise<Contact[]> {
    const all = this.read();
    const settings = await this.getSettings();
    if (query.phoneNumber) {
      const parsed = parsePhoneNumberFromString(
        query.phoneNumber,
        settings.defaultCountry as CountryCode
      );
      const target = parsed?.isValid() ? parsed.number : null;
      if (target) {
        return all.filter((c) => c.phoneNumbers.some((p) => p.e164 === target));
      }
      // Fall back to digits-only suffix match
      const suffix = query.phoneNumber.replace(/\D+/g, '').slice(-9);
      if (suffix) {
        return all.filter((c) =>
          c.phoneNumbers.some((p) => (p.e164 ?? p.number).replace(/\D+/g, '').endsWith(suffix))
        );
      }
    }
    if (query.email) {
      const target = query.email.trim().toLowerCase();
      return all.filter((c) =>
        c.emails.some((e) => e.address.trim().toLowerCase() === target)
      );
    }
    return [];
  }

  async renormaliseAll() {
    const all = this.read();
    const settings = await this.getSettings();
    let updated = 0;
    let skipped = 0;
    const next = all.map((c) => {
      const normalised = normalisePhones(c.phoneNumbers, settings.defaultCountry);
      if (JSON.stringify(normalised) === JSON.stringify(c.phoneNumbers)) {
        skipped++;
        return c;
      }
      updated++;
      return { ...c, phoneNumbers: normalised, updatedAt: new Date().toISOString() };
    });
    this.write(next);
    return { total: all.length, updated, skipped };
  }

  async checkDuplicates(input: {
    phoneNumbers?: { number: string }[];
    emails?: { address: string }[];
    excludeId?: string;
  }): Promise<DuplicateMatch[]> {
    const all = this.read().filter((c) => !c.deletedAt);
    const settings = await this.getSettings();
    const phoneTargets = new Set<string>();
    for (const p of input.phoneNumbers ?? []) {
      const parsed = parsePhoneNumberFromString(p.number, settings.defaultCountry as CountryCode);
      if (parsed?.isValid()) phoneTargets.add(parsed.number);
    }
    const emailTargets = new Set<string>();
    for (const e of input.emails ?? []) {
      if (e.address.trim()) emailTargets.add(e.address.trim().toLowerCase());
    }
    const matches: DuplicateMatch[] = [];
    for (const c of all) {
      if (c.id === input.excludeId) continue;
      let matched = false;
      for (const p of c.phoneNumbers) {
        const e164 = p.e164 ?? null;
        if (e164 && phoneTargets.has(e164)) {
          matches.push({ id: c.id, displayName: c.displayName, reason: 'phone', matchedValue: e164 });
          matched = true;
          break;
        }
      }
      if (matched) continue;
      for (const e of c.emails) {
        const addr = e.address.trim().toLowerCase();
        if (addr && emailTargets.has(addr)) {
          matches.push({ id: c.id, displayName: c.displayName, reason: 'email', matchedValue: addr });
          break;
        }
      }
    }
    return matches;
  }

  async uploadPhoto(id: string, file: Blob): Promise<Contact> {
    const all = this.read();
    const idx = all.findIndex((c) => c.id === id);
    if (idx === -1) throw new Error('Not found');
    const dataUrl = await blobToDataUrl(file);
    const updated: Contact = {
      ...all[idx]!,
      photoUrl: dataUrl,
      updatedAt: new Date().toISOString()
    };
    all[idx] = updated;
    this.write(all);
    return updated;
  }

  async deletePhoto(id: string): Promise<Contact> {
    const all = this.read();
    const idx = all.findIndex((c) => c.id === id);
    if (idx === -1) throw new Error('Not found');
    const { photoUrl: _drop, ...rest } = all[idx]!;
    const updated: Contact = { ...rest, updatedAt: new Date().toISOString() };
    all[idx] = updated;
    this.write(all);
    return updated;
  }

  async scanBusinessCard(_file: Blob): Promise<Partial<NewContact>> {
    throw new Error('Business-card scanning is unavailable in demo mode.');
  }

  async merge(keepId: string, mergeId: string): Promise<Contact> {
    if (keepId === mergeId) throw new Error('keepId and mergeId must differ');
    const all = this.read();
    const keep = all.find((c) => c.id === keepId);
    const drop = all.find((c) => c.id === mergeId);
    if (!keep || !drop) throw new Error('Contact not found');
    const phoneSeen = new Set(keep.phoneNumbers.map((p) => p.e164 ?? p.number));
    const phoneNumbers = [...keep.phoneNumbers];
    for (const p of drop.phoneNumbers) {
      const k = p.e164 ?? p.number;
      if (!phoneSeen.has(k)) {
        phoneNumbers.push(p);
        phoneSeen.add(k);
      }
    }
    const emailSeen = new Set(keep.emails.map((e) => e.address.trim().toLowerCase()));
    const emails = [...keep.emails];
    for (const e of drop.emails) {
      const k = e.address.trim().toLowerCase();
      if (k && !emailSeen.has(k)) {
        emails.push(e);
        emailSeen.add(k);
      }
    }
    const merged: Contact = {
      ...keep,
      phoneNumbers,
      emails,
      tags: Array.from(new Set([...(keep.tags ?? []), ...(drop.tags ?? [])])),
      favoritedBy: Array.from(
        new Set([...(keep.favoritedBy ?? []), ...(drop.favoritedBy ?? [])])
      ),
      nicknames: { ...(drop.nicknames ?? {}), ...(keep.nicknames ?? {}) },
      custom: { ...(drop.custom ?? {}), ...(keep.custom ?? {}) },
      notes: [keep.notes, drop.notes].filter(Boolean).join('\n\n— merged —\n').trim() || undefined,
      website: keep.website ?? drop.website,
      company: keep.company ?? drop.company,
      jobTitle: keep.jobTitle ?? drop.jobTitle,
      department: keep.department ?? drop.department,
      address: hasAddress(keep.address) ? keep.address : hasAddress(drop.address) ? drop.address : undefined,
      updatedAt: new Date().toISOString()
    };
    const next = all.map((c) =>
      c.id === keepId
        ? merged
        : c.id === mergeId
        ? { ...c, deletedAt: new Date().toISOString(), deletedBy: DEMO_USER_OID }
        : c
    );
    this.write(next);
    return merged;
  }

  async remove(id: string, options: { hard?: boolean } = {}): Promise<void> {
    const all = this.read();
    if (options.hard) {
      this.write(all.filter((c) => c.id !== id));
      return;
    }
    const idx = all.findIndex((c) => c.id === id);
    if (idx === -1) return;
    const existing = all[idx]!;
    if (existing.deletedAt) return;
    all[idx] = {
      ...existing,
      deletedAt: new Date().toISOString(),
      deletedBy: DEMO_USER_OID
    };
    this.write(all);
  }

  async restore(id: string): Promise<Contact> {
    const all = this.read();
    const idx = all.findIndex((c) => c.id === id);
    if (idx === -1) throw new Error('Not found');
    const existing = all[idx]!;
    const { deletedAt: _d, deletedBy: _b, ...rest } = existing;
    const restored: Contact = { ...rest, updatedAt: new Date().toISOString() };
    all[idx] = restored;
    this.write(all);
    return restored;
  }

  async listAuditLog() {
    const raw = localStorage.getItem(DEMO_ACTIVITIES_KEY);
    const all = raw ? (JSON.parse(raw) as Activity[]) : [];
    const audits = all
      .filter((a) => a.type === 'audit')
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
    return { items: audits, continuationToken: null };
  }

  async createMany(inputs: NewContact[]) {
    const created: Contact[] = [];
    const errors: { input: NewContact; error: string }[] = [];
    for (const input of inputs) {
      try {
        created.push(await this.create(input));
      } catch (err) {
        errors.push({ input, error: (err as Error).message });
      }
    }
    return { created, errors };
  }

  async getSettings(): Promise<TenantSettings> {
    const raw = localStorage.getItem(DEMO_SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as TenantSettings;
      return {
        ...parsed,
        customFields: parsed.customFields ?? [],
        smartLists: parsed.smartLists ?? []
      };
    }
    const now = new Date().toISOString();
    const defaults: TenantSettings = {
      id: 'settings',
      tenantId: DEMO_TENANT_ID,
      defaultCountry: 'GB',
      customFields: [],
      smartLists: [],
      createdAt: now,
      updatedAt: now,
      updatedBy: DEMO_USER_OID
    };
    localStorage.setItem(DEMO_SETTINGS_KEY, JSON.stringify(defaults));
    return defaults;
  }

  async updateSettings(patch: TenantSettingsPatch): Promise<TenantSettings> {
    const existing = await this.getSettings();
    const next: TenantSettings = {
      ...existing,
      ...patch,
      updatedAt: new Date().toISOString(),
      updatedBy: DEMO_USER_OID
    };
    localStorage.setItem(DEMO_SETTINGS_KEY, JSON.stringify(next));
    return next;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

function seed(): Contact[] {
  const now = new Date().toISOString();
  const sample: Contact[] = [
    {
      id: crypto.randomUUID(),
      tenantId: DEMO_TENANT_ID,
      displayName: 'Ada Lovelace',
      jobTitle: 'Mathematician',
      department: 'Research',
      company: 'Analytical Engine Co.',
      address: { street: '1 Example Street', city: 'Exampleton', postalCode: 'EX1 1AA', country: 'GB' },
      emails: [{ type: 'work', address: 'ada@analytical.test', primary: true }],
      phoneNumbers: [{ type: 'work', number: '+44 20 7946 0000', primary: true }],
      tags: ['vip', 'analytical-engine'],
      notes: 'First computer programmer.',
      createdAt: now,
      updatedAt: now,
      favoritedBy: [],
      nicknames: {},
      callTreatment: 'normal',
      visibility: 'shared',
      kind: 'person',
      custom: {},
      createdBy: DEMO_USER_OID,
      updatedBy: DEMO_USER_OID
    },
    {
      id: crypto.randomUUID(),
      tenantId: DEMO_TENANT_ID,
      displayName: 'Alan Turing',
      jobTitle: 'Cryptanalyst',
      company: 'Bletchley Park',
      emails: [{ type: 'work', address: 'alan@bletchley.test', primary: true }],
      phoneNumbers: [{ type: 'mobile', number: '+44 7700 900123' }],
      tags: ['research'],
      notes: '',
      createdAt: now,
      updatedAt: now,
      favoritedBy: [],
      nicknames: {},
      callTreatment: 'normal',
      visibility: 'shared',
      kind: 'person',
      custom: {},
      createdBy: DEMO_USER_OID,
      updatedBy: DEMO_USER_OID
    }
  ];
  localStorage.setItem(DEMO_KEY, JSON.stringify(sample));
  return sample;
}

export function createApiClient(options: ApiOptions): ApiClient {
  return options.demo ? new DemoApiClient() : new HttpApiClient(options);
}
